import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { isNativeApp, nativeApp, type NativeListener } from '@/features/location/native-bridge';

/** Where "back" has nowhere left to go. */
const ROOT_PATHS = new Set(['/', '/dashboard']);

/**
 * The Android hardware back button.
 *
 * Without a registered listener the App plugin's callback either goes back in
 * WebView history or does nothing; without the plugin at all Capacitor's
 * default finishes the activity, which is what closed the app from wherever
 * you were standing.
 *
 * The rules, in order:
 *
 * 1. If a dialog, sheet or popover is open, send Escape and let it close.
 *    Radix closes on Escape, so back dismisses the punch dialog rather than
 *    navigating out from under a half-finished punch.
 * 2. Otherwise, if this is not the first entry and not a root screen, go back.
 * 3. Otherwise ask before leaving.
 *
 * The prompt exists because leaving is not free here: the WebView is what
 * uploads the positions the geolocation plugin reports, so closing the app
 * stops a technician's trail until they open it again. One press at the
 * dashboard used to end the shift's reporting silently.
 *
 * Note the prompt is itself a Radix dialog, so rule 1 catches the next press
 * and cancels it — back out of the exit prompt does the harmless thing.
 *
 * Registered once, on mount. An earlier version depended on the current route
 * and so tore the listener down and rebuilt it on every navigation, which is
 * both wasteful and a window in which a press does the old thing. The route is
 * read through refs instead, so the callback always sees the current one
 * without the subscription churning.
 */
export interface AndroidBackButton {
  /** Whether the "leave the app?" prompt should be showing. */
  exitPromptOpen: boolean;
  setExitPromptOpen: (open: boolean) => void;
  /** Closes the app. Falls back to minimising if the shell has no exitApp. */
  confirmExit: () => void;
}

export function useAndroidBackButton(): AndroidBackButton {
  const navigate = useNavigate();
  const location = useLocation();
  const [exitPromptOpen, setExitPromptOpen] = useState(false);

  // Read inside the native callback, which is registered once and would
  // otherwise close over the route as it stood at mount.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    if (!isNativeApp()) return;

    const plugin = nativeApp();
    if (!plugin) return;

    let handle: NativeListener | null = null;
    let cancelled = false;

    const onBack = () => {
      // An open Radix overlay marks its layer with data-state="open"; querying
      // for that is less brittle than tracking every dialog's state up here.
      const overlay = document.querySelector(
        '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"]',
      );

      if (overlay) {
        document.dispatchEvent(
          new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }),
        );
        return;
      }

      const here = locationRef.current;
      // `location.key` is 'default' on the first entry, which is the honest
      // signal for "nothing to go back to" — the plugin's own `canGoBack`
      // describes WebView history, which a client-side router does not always
      // grow.
      const isFirstEntry = here.key === 'default';

      if (!isFirstEntry && !ROOT_PATHS.has(here.pathname)) {
        void navigateRef.current(-1);
        return;
      }

      // Nowhere left to go back to. Ask rather than act: the alternative is a
      // shift's location trail ending on a mis-tap nobody notices.
      setExitPromptOpen(true);
    };

    /*
      The bridge's `addListener` returns a plain `{ remove }` object, not a
      promise — Capacitor's injected plugin proxy calls `Capacitor.addListener`
      directly and hands back the handle synchronously. Awaiting it through
      `Promise.resolve` accepts both that and the real `@capacitor/app` package,
      which does return a promise. Calling `.then` on the raw return threw a
      TypeError inside this effect, which is exactly the bug this replaces.
    */
    void (async () => {
      try {
        const listener = await Promise.resolve(plugin.addListener('backButton', onBack));
        if (cancelled) {
          void listener?.remove?.();
          return;
        }
        handle = listener ?? null;
      } catch {
        // An older shell without the plugin, or a bridge that refused. Back
        // keeps whatever behaviour the shell already had.
      }
    })();

    return () => {
      cancelled = true;
      void handle?.remove?.();
    };
  }, []);

  const confirmExit = useCallback(() => {
    setExitPromptOpen(false);
    const plugin = nativeApp();
    if (!plugin) return;

    // exitApp finishes the activity. minimizeApp is the fallback rather than
    // the preference: a shell old enough to lack exitApp is old enough to lack
    // both, and backgrounding is the less surprising of the two if only one is
    // there.
    const leave = plugin.exitApp ?? plugin.minimizeApp;
    void leave?.call(plugin).catch(() => undefined);
  }, []);

  return { exitPromptOpen, setExitPromptOpen, confirmExit };
}
